import { isDefined } from '@sgme/fp';
import { ProductNodeBuilderProps } from 'components/common-form/builder/form/node/ProductNodeBuilder.model';
import { useBatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useHighlightColor } from 'components/common-form/hooks/useHighlightColor';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { NumberFormatter } from 'components/shared/NumberFormatter/NumberFormatter';
import type { StyledNumberFormatterProps } from 'components/shared/NumberFormatter/NumberFormatter.model';
import { WithMinMax } from 'components/shared/WithMinMax';
import { WithUnitComponent, type WithUnitProps } from 'components/shared/WithUnit/WithUnit';
import type { IntlKey } from 'locales';
import { IntWithUnitsData, IntWithUnitsMetadata } from 'models/forms/fields/IntWithUnitsMetadata';
import { FC, useMemo } from 'react';
import { getTextAlignClassName } from 'shared/utils/getTextAlignClassName';

export const IntWithUnitsField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [value, setValue] = useData<IntWithUnitsData>(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const {
    valueTypes,
    translationKey,
    hasTooltip,
    textAlign,
    minWidth,
    maxWidth,
    tooltipLocation,
    minimum,
    maximum,
    defaultValueType,
    isHighlighted,
  } = useFieldMetadata<IntWithUnitsMetadata>(id)!;
  const {
    onEditingKeyDown: onKeyDown,
    isCellInactive,
    isFocused,
    isSelectAll,
    lostInputBeforeEditing,
  } = useBatchNavigationContext({});

  const highlightColor = useHighlightColor();
  const lostInputValue = useMemo<number | undefined>(() => {
    return lostInputBeforeEditing ? Number(lostInputBeforeEditing) : undefined;
  }, [lostInputBeforeEditing]);

  const props: StyledNumberFormatterProps & WithUnitProps = {
    e2e: id,
    value: lostInputValue ?? value?.value,
    onBlur: (val?: number) => {
      const newValue: Nullable<number> = val ?? null;
      if (newValue !== value?.value) {
        const newSwitchableValue = isDefined(value) ? { ...value, value: newValue } : null;
        setValue(newSwitchableValue);
      }
    },
    isDisabled: workspaceType === 'newBatch' ? isReadOnly : false,
    isReadOnly,
    showUnit: true,
  };
  const tooltipKey = hasTooltip ? (`form.${translationKey}.tooltip` as IntlKey) : undefined;
  const textAlignClassName = getTextAlignClassName(textAlign);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      minWidth={minWidth}
      maxWidth={maxWidth}
      tooltipLocation={tooltipLocation}
      tooltipKey={tooltipKey}
      className={patchedFieldClassName}
    >
      <WithMinMax
        minimum={minimum}
        maximum={maximum}
        Component={WithUnitComponent<StyledNumberFormatterProps>}
        UnitComponent={NumberFormatter}
        units={valueTypes}
        unit={value?.valueType ?? defaultValueType}
        unitsDirection="down"
        onUnitSelected={u => {
          const unitValue = Object.entries(valueTypes ?? []).find(([, v]) => v === u)?.[0];
          if (unitValue) {
            setValue({
              value: null,
              valueType: unitValue as string,
            });
          }
        }}
        {...props}
        className={textAlignClassName}
        tabIndex={tabIndex}
        isHighlighted={isHighlighted ? highlightColor : undefined}
        isCellInactive={isCellInactive}
        onKeyDown={onKeyDown}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
      />
    </BlockOrientationFormGroup>
  );
};

IntWithUnitsField.displayName = 'IntWithUnitsField';
